import tw from 'twin.macro'
import React, { useMemo, useState } from 'react'
import * as Popover from '@radix-ui/react-popover'
import { MdOutlineFilterAlt } from 'react-icons/md'
import { CgChevronLeft } from 'react-icons/cg'

import { useListenProgressionsQuery } from 'modules/Progressions/progressionsApi'
import { useListenExercisesQuery } from 'modules/ExerciseLibrary/exerciseLibraryApi'
import {
  useSetWorkoutExerciseMutation,
  useListenProgramDefaultProgressionTagsQuery,
  useSetProgramDefaultProgressionTagMutation,
} from 'modules/Programs/programApi'

import { Tooltip } from 'common/components/Tooltip/Tooltip'
import { ScrollArea } from 'common/components/ScrollArea/ScrollArea'
import { Spinner } from 'common/components/Spinner/Spinner'
import { useCustomization } from 'common/contexts/Customization/useCustomization'

const popoverBtnCss = tw`
  flex items-center bg-white hover:bg-gray-300 hover:bg-opacity-30
  text-sm text-tBlack transition-all rounded-md justify-start
  font-medium px-2 py-2 w-full`

export function DefaultProgressionTagPopover({
  exercise,
  orgId,
  workoutId,
  programId,
  partIdx,
  exIdx,
  isOpen,
  setIsOpen,
}) {
  const { data: programDefaultProgressionTags } = useListenProgramDefaultProgressionTagsQuery({
    orgId,
    programId,
  })
  const defaultProgressionTagsLoading =
    programDefaultProgressionTags === undefined || programDefaultProgressionTags?.isLoading

  const { data: progressions } = useListenProgressionsQuery({ coachOrgId: orgId })
  const progressionsLoading = progressions === undefined || progressions?.isLoading

  const exerciseId = exercise.id

  const hasProgressions = progressionsLoading
    ? false
    : Object.values(progressions).some(
        (exIds) => Array.isArray(exIds) && exIds.length > 1 && exIds.includes(exerciseId)
      )

  const programLevelTag =
    programDefaultProgressionTags && !defaultProgressionTagsLoading
      ? programDefaultProgressionTags[exerciseId] || null
      : null

  const currentDefaultTag = exercise.defaultProgressionTag || programLevelTag
  const tagSource = exercise.defaultProgressionTag ? 'this instance' : programLevelTag ? 'program-wide' : null

  const tooltipContent = currentDefaultTag
    ? `Default progression tag: ${currentDefaultTag} (${tagSource})`
    : 'Set default progression tag'

  const handleOpenChange = (open) => {
    setIsOpen(open)
  }

  if (!hasProgressions) return null

  return (
    <Popover.Root open={isOpen} onOpenChange={handleOpenChange}>
      <Tooltip content={<div className='max-w-96'>{tooltipContent}</div>}>
        <Popover.Trigger asChild tabIndex={-1}>
          <button
            onClick={(e) => e.stopPropagation()}
            css={[
              tw`cursor-pointer flex items-center mr-1 text-gray-500 hover:text-tGreen`,
              Boolean(currentDefaultTag) && tw`text-tGreen`,
            ]}
          >
            <MdOutlineFilterAlt className='w-[18px] h-[18px]' />
          </button>
        </Popover.Trigger>
      </Tooltip>
      <Popover.Portal>
        <Popover.Content
          onClick={(e) => e.stopPropagation()}
          className='bg-white rounded-md shadow-xl border-2 border-gray-300 z-50 min-w-44 max-w-72'
          align='end'
          alignOffset={-7}
          sideOffset={5}
          arrowPadding={12}
        >
          {isOpen && (
            <PopoverContent
              exercise={exercise}
              orgId={orgId}
              workoutId={workoutId}
              programId={programId}
              partIdx={partIdx}
              exIdx={exIdx}
              currentDefaultTag={currentDefaultTag}
              tagSource={tagSource}
              setIsOpen={setIsOpen}
              progressions={progressions}
            />
          )}
          <Popover.Arrow className='fill-gray-300' />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  )
}

function PopoverContent({
  exercise,
  orgId,
  workoutId,
  programId,
  partIdx,
  exIdx,
  currentDefaultTag,
  tagSource,
  setIsOpen,
  progressions,
}) {
  const { exVidOrientation } = useCustomization()
  const { data: exercisesData } = useListenExercisesQuery({ coachOrgId: orgId, exVidOrientation })
  const [setWorkoutExercise] = useSetWorkoutExerciseMutation()
  const [setProgramDefaultProgressionTag] = useSetProgramDefaultProgressionTagMutation()

  const [pendingTag, setPendingTag] = useState(null)
  const [step, setStep] = useState('tags') // 'tags' | 'scope'

  const exerciseId = exercise.id
  const exercises = useMemo(() => exercisesData?.exercises || {}, [exercisesData?.exercises])

  const isLoading = !exercisesData || exercisesData.isFiller

  const availableTags = useMemo(() => {
    if (isLoading || !exerciseId) return []

    const tags = new Set()
    Object.values(progressions).forEach((exIds) => {
      if (Array.isArray(exIds) && exIds.includes(exerciseId)) {
        exIds.forEach((exId) => {
          const exTags = exercises[exId]?.tags || []
          exTags.forEach((tag) => tags.add(tag))
        })
      }
    })
    return [...tags].sort()
  }, [progressions, exerciseId, exercises, isLoading])

  const handleTagClick = (tag) => {
    setPendingTag(tag)
    setStep('scope')
  }

  const handleScopeSelect = async (scope) => {
    const updatedExercise = { ...exercise, defaultProgressionTag: scope === 'all' ? null : pendingTag }
    await setWorkoutExercise({ orgId, workoutId, partIdx, exIdx, exercise: updatedExercise })
    if (scope === 'all') {
      await setProgramDefaultProgressionTag({ orgId, programId, exId: exerciseId, tag: pendingTag })
    }
    setIsOpen(false)
  }

  if (isLoading) {
    return (
      <div className='flex items-center justify-center p-4'>
        <Spinner className='w-4 h-4 text-gray-200' fillClassName='fill-gray-500' />
      </div>
    )
  }

  if (step === 'scope') {
    return (
      <div className='py-2'>
        <div className='flex items-center px-2 pb-2 border-b border-gray-200'>
          <button onClick={() => setStep('tags')} className='text-gray-500 hover:text-tGreen mr-1.5 flex items-center'>
            <CgChevronLeft className='w-5 h-5' />
          </button>
          <span className='text-sm text-gray-500 font-medium truncate'>{pendingTag || 'No default tag'}</span>
        </div>
        <div className='pl-2 pr-4 pt-2'>
          <button onClick={() => handleScopeSelect('single')} css={popoverBtnCss}>
            Just this instance
          </button>
          <button onClick={() => handleScopeSelect('all')} css={popoverBtnCss}>
            All instances in program
          </button>
        </div>
      </div>
    )
  }

  if (availableTags.length === 0) {
    return (
      <div className='px-3 py-4'>
        <h5 className='text-sm text-tBlack font-medium mb-1'>Progression exercises have no tags</h5>
        <p className='text-xs text-gray-500'>
          Tags can be added to each exercise individually in the Exercise Library.
        </p>
      </div>
    )
  }

  return (
    <ScrollArea css={tw`max-h-80`}>
      <div className='pl-2 pr-4 py-2'>
        <button onClick={() => handleTagClick(null)} css={[popoverBtnCss, !currentDefaultTag && tw`text-tGreen`]}>
          No default tag
        </button>
      </div>
      <div className='h-[1px] bg-gray-300' />
      <div className='pl-2 pr-4 py-2'>
        {availableTags.map((tag) => (
          <button
            key={tag}
            onClick={() => handleTagClick(tag)}
            css={[popoverBtnCss, tw`items-center`, currentDefaultTag === tag && tw`text-tGreen`]}
            className='group'
          >
            <span className='flex-1 break-words text-left'>{tag}</span>
            {currentDefaultTag === tag && tagSource && (
              <span className='shrink-0 ml-1.5 text-xxs text-gray-500 bg-gray-100 group-hover:bg-white px-1.5 py-0.5 rounded font-normal'>
                {tagSource}
              </span>
            )}
          </button>
        ))}
      </div>
    </ScrollArea>
  )
}
